import React, {useEffect, useState} from 'react'
import styled from 'styled-components'
import {
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Fade,
  Tooltip,
} from '@material-ui/core'
import DonutChart from '../charts/DonutChart'
import { ImSortAmountAsc, ImSortAmountDesc, ImSortAlphaAsc, ImSortAlphaDesc, ImSortNumericAsc, ImSortNumbericDesc } from 'react-icons/im'

const StyledTableRow = styled(TableRow)(({theme}) => ({
  '&:last-child td, &:last-child th': {
    border: 0,
  }
}))

const RoiTable = (props) => {  
  const {data, title, colors, tableName, roi, dark} = props
  // const tableHeaders = ["Type", "Mailed", "Spend", "Responses", "Sales", "Response Rate", "Holdout Sales", "Holdout Count", "Holdout Responses", "Holdout Response Rate", "Inc Response Rate", "Avg Ticket", "ROAS", "Inc ROAS"]
  const tableHeaders = [
    {header: "Type", description: "Label"},
    {header: "Mailed", description: ""},
    {header: "Spend", description: "Cost"},
    {header: "Responses", description: ""},
    {header: "Sales", description: ""},
    {header: "Response Rate", description: "Res_Rate"},
    {header: "Holdout Sales", description: "HO_Sales"},
    {header: "Holdout Count", description: "HO_Count"},
    {header: "Holdout Responses", description: "HO_Resp"},
    {header: "Holdout Response Rate", description: "HO_Res_Rate"},
    {header: "Inc Response Rate", description: "ADJ_Res_Rate"},
    {header: "Avg Ticket", description: "Avg_Ticket"},
  ]
  const alternateHeaders = []
  const [dataOrdered, setDataOrdered] = useState(data.length > 0 ? data : [])
  const [donutSeries, setDonutSeries] = useState([])  
  const [orderBy, setOrderBy] = useState("Label")
  const [orderAsc, setOrderAsc] = useState(true)
  const [rowsPerPage, setRowsPerPage] = useState(data.length > 20 ? Math.round((data.length / 4) / 10) * 10 : Math.round((data.length / 4) / 5) * 5)
  const [page, setPage] = useState(0)
  const [chartColors, setChartColors] = useState([])

  useEffect(() => {    
    data.forEach(({Mailed, Responses, Sales, HO_Count, HO_Resp, Cost}, i) => {      
      data[i]["Res_Rate"] = Mailed > 0 ? Responses / Mailed : 0
      data[i]["HO_Res_Rate"] = HO_Count > 0 ? HO_Resp / HO_Count : 0
      data[i]["ADJ_Res_Rate"] = data[i]["Res_Rate"] - data[i]["HO_Res_Rate"] > 0 ? data[i]["Res_Rate"] - data[i]["HO_Res_Rate"] : 0
      data[i]["Avg_Ticket"] = Responses > 0 ?  Sales / Responses : 0
      data[i]["ROAS"] = Cost > 0 ? Sales/Cost : 0
      data[i]["ADJ_ROAS"] = HO_Count > 0 ? data[i]["ADJ_Res_Rate"] > 0 ? Sales / data[i]["Res_Rate"] * (data[i]["ADJ_Res_Rate"] / Cost) : 0 : Sales / Cost
    })    
    
    data.sort((a,b) => b.Sales - a.Sales).slice(0, 9).forEach((item, i) => {
      item["color"] = colors ? colors[i] : null
    })

    data.filter((item, i) => !item.color).forEach((item) => {
      item["color"] = "#c3a6f7"
    })    
        
    if(orderBy === "Label" && orderAsc === true) data.sort((a,b) => a.Label.localeCompare(b.Label))
    else if(orderBy === "Label" && !orderAsc) data.sort((a,b) => b.Label.localeCompare(a.Label))
    else if(orderBy !== "Label" && orderAsc) data.sort((a,b) => a[orderBy] - b[orderBy])
    else data.sort((a,b) => b[orderBy] - a[orderBy])
    setDonutSeries(data.map(({Sales}) => Math.round(Sales)))
  }, [data])  

  let totalsRow = data.length > 0 ? data.reduce((a,b) => {
    for (const prop in b) {
      if(a.hasOwnProperty(prop)) a[prop] += b[prop]
      else a[prop] = b[prop]
    }
    return a
  }, {}) : {}  

  const handleChangePage = (e, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (e) => {
    const { value } = e.target
    setRowsPerPage(value)
    setPage(0)
  }

  const handleSortColumn = (columnName) => {
    let description = ""
    const descriptionHash = {
      "Type": "Label",
      "Spend": "Cost",
      "Response Rate": "Res_Rate",
      "Holdout Count": "HO_Count",
      "Holdout Sales": "HO_Sales",
      "Holdout Responses": "HO_Resp",
      "Holdout Response Rate": "HO_Res_Rate",
      "Inc Response Rate": "ADJ_Res_Rate",
      "Avg Ticket": "Avg_Ticket",
    }    
    
    if(descriptionHash.hasOwnProperty(columnName)) description = descriptionHash[columnName]
    else description = columnName

    if(description === orderBy) {      
      if(orderAsc && description === "Label") {        
        setDataOrdered(data.sort((a,b) => b[description].localeCompare(a[description])))
      } else if(description === "Label") {        
        setDataOrdered(data.sort((a,b) => a[description].localeCompare(b[description])))
      } else if (orderAsc) {        
        setDataOrdered(data.sort((a,b) => b[description] - a[description]))
      } else setDataOrdered(data.sort((a,b) => a[description] - b[description]))
      setOrderBy(description)
      setOrderAsc(!orderAsc)
    } else {      
      if(description === "Label") setDataOrdered(data.sort((a,b) => a[description].localeCompare(b[description])))
      else setDataOrdered(data.sort((a,b) => a[description] - b[description]))
      setOrderBy(description)
      setOrderAsc(true)
    }
  }

  return (
    <div className="roi-table">
      <Typography className={`roi-table-title ${dark ? "content-dk" : "content-lt"}`} variant="caption">{roi === "triggers" && title === "Product Categories" ? "TRIGGER TYPES" : title.toUpperCase()}</Typography>
      <Paper className={`roi-table-paper ${dark ? "bg-dk" : "bg-lt"}`}>        
        <Table
          style={title !== "Locations" ? {borderRight: "1px solid #25aae1", minHeight: "300px"} : {}}
          className="table"
        >
          <TableHead>            
            <TableRow className="roi-table-head">
              {tableHeaders.map(({header, description}, i) => (
                <TableCell
                  key={`${header}-${i}`}
                  onClick={() => handleSortColumn(`${header}`)}
                  align={i > 0 ? "right" : "left"}
                  style={i == 0 ? {borderTopLeftRadius: "10px"} : i == tableHeaders.length - 1 && title === "Locations" ? {borderTopRightRadius: "10px"} : {}}
                  size="small"
                >                  
                  <div className="roi-table-headers">
                    <Typography variant="caption">                      
                      {header}
                    </Typography>
                    {
                      orderBy === header || orderBy === description ? orderAsc ? <ImSortAmountAsc style={{fontSize: "15px", marginLeft: "5px"}} /> : <ImSortAmountDesc style={{fontSize: "15px", marginLeft: "5px"}} />
                    : null
                    }
                  </div>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>                    
          <TableBody>            
            {data.length <= 10 ?            
              data.map((item, i) => (
                <StyledTableRow key={`${item.Label}-${i}`}>
                  <TableCell><Typography variant="caption" className={`${dark ? "roi-table-body" : "roi-table-body-lt"}`} style={colors && title !== "Locations" ? {color: `${item.color}`} : {}}>{item.Label.toUpperCase()}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="caption" className={`${dark ? "roi-table-body" : "roi-table-body-lt"}`}>{item.Mailed ? Math.round(item.Mailed).toLocaleString("en") : 0}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="caption" className={`${dark ? "roi-table-body" : "roi-table-body-lt"}`}>${item.Cost ? Math.round(item.Cost).toLocaleString("en") : 0}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="caption" className={`${dark ? "roi-table-body" : "roi-table-body-lt"}`}>{item.Responses ? Math.round(item.Responses).toLocaleString("en") : 0}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="caption" className={`${dark ? "roi-table-body" : "roi-table-body-lt"}`}>${item.Sales ? Math.round(item.Sales).toLocaleString("en") : 0}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="caption" className={`${dark ? "roi-table-body" : "roi-table-body-lt"}`}>{item.Res_Rate ? (item.Res_Rate * 100).toFixed(2) : 0}%</Typography></TableCell>
                  <TableCell align="right"><Typography variant="caption" className={`${dark ? "roi-table-body" : "roi-table-body-lt"}`}>{item.HO_Sales ? Math.round(item.HO_Sales).toLocaleString("en") : 0}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="caption" className={`${dark ? "roi-table-body" : "roi-table-body-lt"}`}>{item.HO_Count ? Math.round(item.HO_Count).toLocaleString("en") : 0}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="caption" className={`${dark ? "roi-table-body" : "roi-table-body-lt"}`}>{item.HO_Resp ? Math.round(item.HO_Resp).toLocaleString("en") : 0}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="caption" className={`${dark ? "roi-table-body" : "roi-table-body-lt"}`}>{item.HO_Res_Rate ? (item.HO_Res_Rate * 100).toFixed(2) : 0}%</Typography></TableCell>
                  <TableCell align="right"><Typography variant="caption" className={`${dark ? "roi-table-body" : "roi-table-body-lt"}`}>{item.ADJ_Res_Rate > 0 ? (item.ADJ_Res_Rate * 100).toFixed(2) : 0.00}%</Typography></TableCell>
                  <TableCell align="right"><Typography variant="caption" className={`${dark ? "roi-table-body" : "roi-table-body-lt"}`}>${item.Avg_Ticket ? Math.round(item.Avg_Ticket).toLocaleString("en") : 0}</Typography></TableCell>
                </StyledTableRow>
              ))                        
            :
              data.slice(page * 10, page * 10 + 10)
              .map((item, i) => (
                <StyledTableRow key={`${item.label}-${i}`}>
                  <TableCell><Typography variant="caption" className={`${dark ? "roi-table-body" : "roi-table-body-lt"}`} style={colors && title !== "Locations" ? {color: `${item.color}`} : {}}>{item.Label.toUpperCase()}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="caption" className={`${dark ? "roi-table-body" : "roi-table-body-lt"}`}>{item.Mailed ? Math.round(item.Mailed).toLocaleString("en") : 0}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="caption" className={`${dark ? "roi-table-body" : "roi-table-body-lt"}`}>${item.Cost ? Math.round(item.Cost).toLocaleString("en") : 0}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="caption" className={`${dark ? "roi-table-body" : "roi-table-body-lt"}`}>{item.Responses ? Math.round(item.Responses).toLocaleString("en") : 0}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="caption" className={`${dark ? "roi-table-body" : "roi-table-body-lt"}`}>${item.Sales ? Math.round(item.Sales).toLocaleString("en") : 0}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="caption" className={`${dark ? "roi-table-body" : "roi-table-body-lt"}`}>{item.Res_Rate ? (item.Res_Rate * 100).toFixed(2) : 0}%</Typography></TableCell>
                  <TableCell align="right"><Typography variant="caption" className={`${dark ? "roi-table-body" : "roi-table-body-lt"}`}>{item.HO_Sales ? Math.round(item.HO_Sales).toLocaleString("en") : 0}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="caption" className={`${dark ? "roi-table-body" : "roi-table-body-lt"}`}>{item.HO_Count ? Math.round(item.HO_Count).toLocaleString("en") : 0}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="caption" className={`${dark ? "roi-table-body" : "roi-table-body-lt"}`}>{item.HO_Resp ? Math.round(item.HO_Resp).toLocaleString("en") : 0}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="caption" className={`${dark ? "roi-table-body" : "roi-table-body-lt"}`}>{item.HO_Res_Rate ? (item.HO_Res_Rate * 100).toFixed(2) : 0}%</Typography></TableCell>
                  <TableCell align="right"><Typography variant="caption" className={`${dark ? "roi-table-body" : "roi-table-body-lt"}`}>{item.ADJ_Res_Rate > 0 ? (item.ADJ_Res_Rate * 100).toFixed(2) : 0}%</Typography></TableCell>
                  <TableCell align="right"><Typography variant="caption" className={`${dark ? "roi-table-body" : "roi-table-body-lt"}`}>${item.Avg_Ticket ? Math.round(item.Avg_Ticket).toLocaleString("en") : 0}</Typography></TableCell>
                </StyledTableRow>
              ))
            }
            {title !== "Locations" ?
              <StyledTableRow className="roi-grand-total">
                <TableCell><Typography variant="caption" className={`total-row ${dark ? "roi-table-body" : "roi-table-body-lt"}`}>GRAND TOTAL</Typography></TableCell>
                <TableCell align="right"><Typography variant="caption" className={`total-row ${dark ? "roi-table-body" : "roi-table-body-lt"}`}>{Math.round(totalsRow.Mailed).toLocaleString("en")}</Typography></TableCell>
                <TableCell align="right"><Typography variant="caption" className={`total-row ${dark ? "roi-table-body" : "roi-table-body-lt"}`}>${Math.round(totalsRow.Cost).toLocaleString("en")}</Typography></TableCell>
                <TableCell align="right"><Typography variant="caption" className={`total-row ${dark ? "roi-table-body" : "roi-table-body-lt"}`}>{Math.round(totalsRow.Responses).toLocaleString("en")}</Typography></TableCell>
                <TableCell align="right"><Typography variant="caption" className={`total-row ${dark ? "roi-table-body" : "roi-table-body-lt"}`}>${Math.round(totalsRow.Sales).toLocaleString("en")}</Typography></TableCell>
                <TableCell align="right"><Typography variant="caption" className={`total-row ${dark ? "roi-table-body" : "roi-table-body-lt"}`}>{((totalsRow.Responses / totalsRow.Mailed) * 100).toFixed(2)}%</Typography></TableCell>
                <TableCell align="right"><Typography variant="caption" className={`total-row ${dark ? "roi-table-body" : "roi-table-body-lt"}`}>${Math.round(totalsRow.HO_Sales).toLocaleString("en")}</Typography></TableCell>
                <TableCell align="right"><Typography variant="caption" className={`total-row ${dark ? "roi-table-body" : "roi-table-body-lt"}`}>{Math.round(totalsRow.HO_Count).toLocaleString("en")}</Typography></TableCell>
                <TableCell align="right"><Typography variant="caption" className={`total-row ${dark ? "roi-table-body" : "roi-table-body-lt"}`}>{Math.round(totalsRow.HO_Resp).toLocaleString("en")}</Typography></TableCell>
                <TableCell align="right"><Typography variant="caption" className={`total-row ${dark ? "roi-table-body" : "roi-table-body-lt"}`}>{((totalsRow.HO_Resp / totalsRow.HO_Count) * 100).toFixed(2)}%</Typography></TableCell>
                <TableCell align="right"><Typography variant="caption" className={`total-row ${dark ? "roi-table-body" : "roi-table-body-lt"}`}>{totalsRow.HO_Count > 0 ? (totalsRow.Responses / totalsRow.Mailed) - (totalsRow.HO_Resp / totalsRow.HO_Count) > 0 ? (((totalsRow.Responses / totalsRow.Mailed) * 100) - ((totalsRow.HO_Resp / totalsRow.HO_Count) * 100)).toFixed(2) : 0 : ((totalsRow.Responses / totalsRow.Mailed) * 100).toFixed(2)}%</Typography></TableCell>
                <TableCell align="right"><Typography variant="caption" className={`total-row ${dark ? "roi-table-body" : "roi-table-body-lt"}`}>${Math.round(totalsRow.Sales / totalsRow.Responses).toLocaleString('en')}</Typography></TableCell>
                {/* <TableCell align="right"><Typography variant="caption" className="roi-table-total-row">${totalsRow.Cost > 0 ? (totalsRow.Sales / totalsRow.Cost).toLocaleString('en', {minimumFractionDigits: 2, maximumFractionDigits: 2}) : 0}</Typography></TableCell>
                <TableCell align="right"><Typography variant="caption" className="roi-table-total-row">${totalsRow.HO_Count > 0 ?  (totalsRow.Responses/totalsRow.Mailed) - (totalsRow.HO_Resp / totalsRow.HO_Count) > 0 ? (totalsRow.Sales / (totalsRow.Responses / totalsRow.Mailed) * (totalsRow.Responses / totalsRow.Mailed - totalsRow.HO_Resp / totalsRow.HO_Count) / totalsRow.Cost).toLocaleString("en", {minimumFractionDigits: 2, maximumFractionDigits: 2}) : 0 : (totalsRow.Sales / totalsRow.Cost).toLocaleString("en", {minimumFractionDigits: 2, maximumFractionDigits: 2})}</Typography></TableCell> */}
              </StyledTableRow>
            : null
            }
          </TableBody>
        </Table>
        {title !== "Locations" ?
          <div className="roi-table-chart">          
            <Typography style={{color: `${dark ? "#fff" : "#6e6e6e"}`, fontWeight: "bold"}} variant="caption">SALES BY {title.toUpperCase()}</Typography>
            <DonutChart
              data={donutSeries}
              total={false}
              radius={"75%"}
              labels={data.map(({Label}) => Label.toUpperCase() + ' SALES')}
              labelShow={true}
              position={"bottom"}
              height={180}
              hoverBorderWidth={5}              
              colors={data.map(({color}) => color)}
              darkMode={dark}
            />
          </div>
        : null }
      </Paper>
      {data.length > 10 ?
        <TablePagination
          rowsPerPageOptions={[]}          
          component="div"
          count={data.length}
          rowsPerPage={10}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          style={{color: `${dark ? "#fff" : "#6e6e6e"}`}}
        />
        : null
      }
    </div>
  )
}

export default RoiTable
